import * as React from "react";
import { ChevronDownIcon } from "lucide-react";

import { cn } from "../../lib/utils";

/**
 * The 32px header that introduces a stacked page section —
 * `— Milestones ———— 3/7 —` in the wireframe. Contract:
 * `docs/project-detail-flow.md` §4.1.
 *
 * This is deliberately *not* `GroupHeader`. `GroupHeader` is grouped-list
 * scaffolding: it always renders a leading dot, its `count` is a bare number,
 * it is sticky by default, and its `actions` slot is `opacity-0` until hover.
 * A page section needs the opposite on every one of those points — a persistent
 * ratio (`3/7`), a persistent inline action, no dot, and no stickiness (four
 * stacked sticky headers in one scroll container is a bug, not a feature).
 */

export interface SectionHeaderProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "title"
> {
  /** Section name, e.g. "Milestones". */
  label: string;
  /** Always-visible tertiary meta after the label: `3/7`, `6`, `2 of 11`. */
  meta?: React.ReactNode;
  /** Rendered right of `meta` in the danger colour — e.g. `3 overdue`. */
  badge?: React.ReactNode;
  /** Always-visible right-aligned controls (`IconButton`, `Button size="xs"`). */
  actions?: React.ReactNode;
  /** Transient status text, e.g. the autosave "Saved" hint. */
  hint?: React.ReactNode;
  /** Optional leading glyph. There is no default dot. */
  icon?: React.ReactNode;
  /** `"sub"` renders the nested (uppercase eyebrow) step down. Default `"section"`. */
  level?: "section" | "sub";
  /** Render the label as a disclosure toggle. Default false. */
  collapsible?: boolean;
  /** Current open state — drives the chevron and `aria-expanded`. */
  open?: boolean;
  onToggle?: () => void;
  /** `id` for the heading element. Pair with `<PageSection aria-labelledby>`. */
  headingId?: string;
  /** `id` of the region the disclosure controls. */
  controls?: string;
  /** Class overrides for the heading text. */
  labelClassName?: string;
}

const SectionHeader = React.forwardRef<HTMLDivElement, SectionHeaderProps>(
  (
    {
      label,
      meta,
      badge,
      actions,
      hint,
      icon,
      level = "section",
      collapsible = false,
      open = true,
      onToggle,
      headingId,
      controls,
      labelClassName,
      className,
      ...props
    },
    ref,
  ) => {
    const Heading: React.ElementType = level === "sub" ? "h3" : "h2";

    const headingClass =
      level === "sub"
        ? "truncate text-xs font-semibold uppercase tracking-wide text-text-tertiary"
        : "truncate text-xs font-semibold text-text";

    const labelNode = (
      <>
        {collapsible ? (
          <ChevronDownIcon
            aria-hidden="true"
            data-open={open ? "true" : "false"}
            className={cn(
              "size-3 shrink-0 text-text-tertiary",
              "transition-transform duration-150 ease-out-fast",
              "data-[open=false]:-rotate-90",
            )}
          />
        ) : null}
        {icon ? (
          <span className="flex shrink-0 items-center [&_svg]:size-3.5 [&_svg]:text-text-tertiary">
            {icon}
          </span>
        ) : null}
        <Heading id={headingId} className={cn(headingClass, labelClassName)}>
          {label}
        </Heading>
      </>
    );

    return (
      <div
        ref={ref}
        data-slot="section-header"
        className={cn(
          // `px-4` at every width, deliberately: a section header labels the
          // rows beneath it, and `ListRow` is `px-4` edge-to-edge. A `md:px-6`
          // header over `px-4` rows puts a 12px kink in the page's left edge.
          // `rounded-lg` because the tint makes this a visible block: a
          // square-cornered band inside a `rounded-xl` sheet reads as a
          // seam rather than a label (borderless contract §4).
          "flex h-control-lg items-center gap-2 rounded-lg bg-page px-4",
          className,
        )}
        {...props}
      >
        {collapsible ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={controls}
            className={cn(
              "-mx-1 flex min-w-0 items-center gap-2 rounded-sm px-1 text-left",
              "focus-visible:outline-none focus-bar",
            )}
          >
            {labelNode}
          </button>
        ) : (
          <span className="flex min-w-0 items-center gap-2">{labelNode}</span>
        )}

        {meta !== undefined && meta !== null && meta !== false ? (
          <span className="flex shrink-0 items-center gap-1 text-xs tabular-nums text-text-tertiary">
            {meta}
          </span>
        ) : null}

        {badge ? (
          <span className="shrink-0 text-xs font-medium tabular-nums text-danger">
            {badge}
          </span>
        ) : null}

        {hint ? (
          <span className="shrink-0 text-xs text-text-tertiary">{hint}</span>
        ) : null}

        {actions ? (
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {actions}
          </div>
        ) : null}
      </div>
    );
  },
);
SectionHeader.displayName = "SectionHeader";

export interface PageSectionProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Separate this section from the one above it. Default true — pass false for
   * the first section on the page.
   */
  separated?: boolean;
}

/**
 * A stacked page section: a `SectionHeader` and its rows. No `Card`, no rounded
 * container, no shadow, no `space-y-6` — sections are separated by space and a
 * label, never by a box or a rule.
 *
 * Borderless (`docs/borderless-contract.md` §1): the old `border-t border-line`
 * rule becomes space.
 *
 * NO SURFACE OF ITS OWN — deliberately transparent. This briefly carried
 * `bg-page` on the theory that a canvas-tinted band would separate it from the
 * sheet, and it read as a grey stripe across the page for every section on
 * every page: the label already names the section and the `mt-2` gap already
 * separates it (§2.1 — a page is a document, not a stack of tinted bands). A
 * section that needs its own surface is a `Card`, and it should say so.
 */
const PageSection = React.forwardRef<HTMLElement, PageSectionProps>(
  ({ separated = true, className, ...props }, ref) => (
    <section
      ref={ref}
      data-slot="page-section"
      className={cn(separated && "mt-2", className)}
      {...props}
    />
  ),
);
PageSection.displayName = "PageSection";

export { SectionHeader, PageSection };
